<script setup lang="ts">
import {ref} from 'vue';
import {storeToRefs} from 'pinia';
import {Icon} from '@iconify/vue';
import {useFileHistoryStore} from '@/stores/useFileHistoryStore';
import FileHistoryGroup from '@/components/file-history/FileHistoryGroup.vue';
import FileHistoryDetailDialog from '@/components/file-history/FileHistoryDetailDialog.vue';

const historyStore = useFileHistoryStore();
const {groups, isEmpty, isSupported, selectedEntry} = storeToRefs(historyStore);

const confirmingClear = ref(false);

function requestClear() {
  confirmingClear.value = true;
}

function cancelClear() {
  confirmingClear.value = false;
}

async function confirmClear() {
  await historyStore.clearAll();
  confirmingClear.value = false;
}
</script>

<template>
  <section class="mx-auto max-w-3xl space-y-4">
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-100">
          <Icon icon="fa6-solid:clock-rotate-left" class="text-base" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900">Verlauf</h2>
          <p class="text-xs text-gray-500">Lokal im Browser gespeichert – bleibt nach Neuladen erhalten.</p>
        </div>
      </div>

      <div v-if="!isEmpty" class="flex items-center gap-2">
        <template v-if="confirmingClear">
          <span class="text-xs text-gray-500">Sicher?</span>
          <button type="button" class="btn btn-secondary !px-3 !py-1.5" @click="cancelClear">Abbrechen</button>
          <button
              type="button"
              class="btn !px-3 !py-1.5 bg-red-600 text-white hover:bg-red-700"
              @click="confirmClear"
          >
            Alle löschen
          </button>
        </template>
        <button
            v-else
            type="button"
            class="btn btn-secondary !px-3 !py-1.5"
            @click="requestClear"
        >
          <Icon icon="fa6-solid:trash" class="mr-1.5 text-sm" />
          Alle löschen
        </button>
      </div>
    </div>

    <!-- OPFS/IndexedDB nicht verfügbar -->
    <div
        v-if="!isSupported"
        class="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
    >
      <Icon icon="fa6-solid:triangle-exclamation" class="shrink-0 text-base" />
      Der Verlauf ist in diesem Browser nicht verfügbar (lokaler Speicher gesperrt).
    </div>

    <!-- Leerer Zustand -->
    <div
        v-else-if="isEmpty"
        class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 px-6 py-12 text-center"
    >
      <Icon icon="fa6-solid:folder-open" class="text-3xl text-gray-300" />
      <p class="text-sm font-medium text-gray-500">Noch keine Dateien im Verlauf</p>
      <p class="max-w-sm text-xs text-gray-400">
        Hochgeladene, transponierte und konvertierte Dateien erscheinen hier automatisch und können
        wiederverwendet oder heruntergeladen werden.
      </p>
    </div>

    <!-- Gruppenliste -->
    <div v-else class="space-y-3">
      <FileHistoryGroup
          v-for="(group, index) in groups"
          :key="group.root.id"
          :group="group"
          :default-open="index === 0"
      />
    </div>

    <!-- Detail-/Vorschau-Dialog (pro Eintrag frisch gemountet → sauberer OSMD-Lebenszyklus) -->
    <FileHistoryDetailDialog v-if="selectedEntry" :key="selectedEntry.id" :entry="selectedEntry" />
  </section>
</template>
